import { getScoreColor } from '$components/SPKCandidateList/SPKPanel/SPKSmartScores/SPKSmartScores';
import {
  CandidateAIScoreType,
  GetCandidateAiScoreOutput,
  useOverrideAiScoresBySlugMutation,
} from '$store/modules/candidate-ai';
import { successHandler } from '$store/utils';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

const SingleAiScoreOverride = ({
  scoreData,
  candidateId,
  groupName,
  tabbableData,
  index,
  onTabToNextInput,
}: {
  scoreData: GetCandidateAiScoreOutput[number];
  candidateId: number;
  groupName: string;
  tabbableData: null | {
    group: string;
    editingIndex: number;
  };
  index: number;
  onTabToNextInput: () => void;
}) => {
  const [isEdit, setIsEdit] = useState(false);
  const { handleSubmit, control, getValues } = useForm({
    defaultValues: { [scoreData.Slug]: scoreData.FinalValue },
  });
  const inputRef = useRef<HTMLInputElement | null>(null);
  const selectRef = useRef<Dropdown | null>(null);

  const [overrideAiScoresBySlug, { isLoading }] =
    useOverrideAiScoresBySlugMutation();

  const onSaveHandler = async (data: any, onSuccess: () => void) => {
    await overrideAiScoresBySlug({
      candidateId,
      ScoreType: scoreData.Slug,
      Value:
        scoreData.ScoreType === CandidateAIScoreType.select
          ? data[scoreData.Slug]
          : data[scoreData.Slug] / 100,
    }).then((resp) => {
      if ('data' in resp) {
        onSuccess();
      }
    });
  };

  const onSuccessSave = () => {
    setIsEdit(false);
    successHandler('Score overrided successfully', true, true);
  };

  const onScoreKeyDownEvent = (ev: any) => {
    if (ev.key === 'Tab') {
      setIsEdit(false);
      onTabToNextInput();
    }
    if (ev.key === 'Enter') {
      ev.preventDefault();
      handleSubmit((data) => {
        const payload = getValues();
        // is select
        if (Number.isNaN(Number(data[scoreData.Slug]))) {
          const currentValue = (
            selectRef.current
              ?.getElement()
              ?.querySelector('.p-dropdown-label') as HTMLDivElement
          )?.innerText;

          payload[scoreData.Slug] = currentValue; // sometimes it stills have the old value, so here im forcing it to have the newest value
        } else {
          // is number

          payload[scoreData.Slug] = parseInt(
            (ev.target as HTMLInputElement).value || '0',
            10
          ); // sometimes it stills have the old value, so here im forcing it to have the newest value
        }
        onSaveHandler(payload, onSuccessSave);
        setIsEdit(false);
        onTabToNextInput();
      })();
    }
  };

  const onEnableEdit = () => {
    setIsEdit(true);
    setTimeout(() => {
      if (inputRef.current) inputRef.current.focus();
      if (selectRef.current) selectRef.current.focus();
    }, 250);
  };

  useEffect(() => {
    if (
      groupName === tabbableData?.group &&
      index === tabbableData?.editingIndex
    ) {
      setIsEdit(true);
      setTimeout(() => {
        if (inputRef?.current) {
          inputRef.current.focus();
          inputRef.current.select();
        } else if (selectRef?.current) {
          selectRef.current.focus();
        }
      }, 250);
    }
  }, [groupName, index, tabbableData, inputRef]);

  return (
    <form
      className={`flex flex-col p-4 min-w-28 rounded relative group ${
        !isEdit && getScoreColor(scoreData.FinalScore)
      }`}
      onSubmit={handleSubmit((data) => onSaveHandler(data, onSuccessSave))}
    >
      <fieldset
        disabled={isLoading}
        className={`${isLoading ? 'opacity-35 hover:cursor-progress' : ''}`}
      >
        <h1 className="text-sm mb-2 hover:cursor-default flex flex-row gap-2">
          <span className="text-nowrap">{scoreData.Name}</span>
          {!isEdit ? (
            <button
              type="button"
              aria-label="edit"
              className="opacity-0 group-hover:opacity-100"
              onClick={onEnableEdit}
            >
              <i className="pi pi-pencil" />
            </button>
          ) : (
            <>
              <button
                type="submit"
                aria-label="Save"
                className="text-center flex justify-center items-center rounded-full hover:text-internal-main opacity-45 hover:opacity-100"
              >
                <i className="pi pi-check" />
              </button>
              <button
                type="button"
                aria-label="cancel"
                onClick={() => setIsEdit(false)}
                className="text-center flex justify-center items-center rounded-full hover:text-spk-brightRed opacity-45 hover:opacity-100"
              >
                <i className="pi pi-times" />
              </button>
            </>
          )}
        </h1>
        {isEdit ? (
          <div className="flex flex-row items-end">
            {scoreData.ScoreType === CandidateAIScoreType.numeric && (
              <Controller
                control={control}
                name={scoreData.Slug}
                render={({ field }) => (
                  <InputNumber
                    inputRef={inputRef}
                    max={100}
                    min={0}
                    autoFocus
                    pt={{
                      input: {
                        root: {
                          className:
                            'border-0 border-b-2 rounded-none !border-b-employer-secondary border-solid enabled:shadow-none enabled:focus:shadow-none max-w-16 !text-3xl py-0',
                        },
                      },
                    }}
                    id={field.name}
                    value={field.value as number}
                    onValueChange={(e) => field.onChange(e.value)}
                    onKeyDown={onScoreKeyDownEvent}
                  />
                )}
              />
            )}
            {scoreData.ScoreType === CandidateAIScoreType.select && (
              <Controller
                control={control}
                name={scoreData.Slug}
                render={({ field }) => (
                  <Dropdown
                    {...field}
                    className="min-w-48"
                    options={
                      'options' in scoreData.Config
                        ? scoreData.Config.options
                        : []
                    }
                    ref={selectRef}
                    onKeyDown={onScoreKeyDownEvent}
                  />
                )}
              />
            )}
          </div>
        ) : (
          <span
            className={`${
              CandidateAIScoreType.numeric === scoreData.ScoreType
                ? 'text-4xl'
                : 'text-xl'
            } hover:cursor-default`}
          >
            {scoreData.FinalValue}{' '}
            {scoreData.OldAiData && <span className="text-xs">(Old)</span>}
          </span>
        )}
      </fieldset>
    </form>
  );
};

SingleAiScoreOverride.defaultProps = {
  scoreClassNames: '',
};

export default SingleAiScoreOverride;
